import { Button, Stack } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { gridChrome } from '../../components/Matrix/gridChrome.js';
import { PullFailedAlert } from '../../components/Matrix/PullFailedAlert.js';
import { MatrixLoadState } from '../../components/Matrix/MatrixLoadState.js';
import { MatrixOverlays } from '../../components/Matrix/MatrixOverlays.js';
import { MatrixPageActions } from '../../components/Matrix/MatrixPageActions.js';
import { MobileFilters } from '../../components/Matrix/MobileFilters.js';
import { isFiltered, visibleColumns } from '../../domain/rowFilter.js';
import { MatrixCardList } from '../../components/Matrix/MatrixCardList.js';
import { MatrixGrid } from '../../components/Matrix/MatrixGrid.js';
import { CONFIG_ROWS } from '../../components/Matrix/rowSpecs.js';
import { useRowFilter } from '../../components/Matrix/useRowFilter.js';
import { PageContainer } from '../../components/PageContainer/PageContainer.js';
import { countOf } from '../../domain/labels.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { useMatrixPageState } from '../../hooks/useMatrixPageState.js';
import { emptyReason, listTotal, masterCount } from '../../domain/emptyReason.js';
import { MatrixEmptyState } from '../../components/Matrix/MatrixEmptyState.js';
import { NEW_CONFIG_PATH } from '../../domain/routes.js';
import { useConfigRows } from '../../hooks/useConfigRows.js';

/**
 * The Configs page: a container, and one kind of row, 50 at a time.
 *
 * It lists master configs and nothing else — the backoffice's global settings, one row per
 * `name`. A config's name leads to its editor. The filter model, the empty states and the
 * selection are each their own thing; what is left here is fetching the roots and arranging them.
 */
export const ConfigsPage = () => {
    const isMobile = useIsMobile();

    const { filters, patch, clear: clearFilters } = useRowFilter();
    const matrix = useMatrixPageState();
    const { selection, setDrawer, pulls } = matrix;

    // `filters.search` is already the debounced value: `DebouncedSearchInput`/`MobileFilters`
    // only patch the URL 300ms after the last keystroke, so reading `filters` straight off the
    // URL here does not need its own debounce on top (that used to double it to ~600ms).
    const roots = useConfigRows(filters);

    const columns = roots.columns;
    // The environment filter focuses the grid on one column; the toolbar still offers them all.
    // Memoised: `visibleColumns` returns a fresh array whenever `environment` narrows to one
    // column, and `MatrixGrid`'s `columns` prop feeds every memoised `MatrixRow`, so a new
    // reference here on every render would defeat that memo just as surely as an unstable
    // callback would.
    const shownColumns = useMemo(() => visibleColumns(columns, filters.environment), [columns, filters.environment]);
    const rows = roots.rows;
    const environmentNames = columns.map((c) => c.name);
    const filtered = isFiltered(filters);
    // Only 50 rows load at a time: past the first page, the header counts what the environments
    // last counted, not what happens to be loaded.
    const totalOf = (loaded: number, type: string) =>
        listTotal({ loaded, hasMore: roots.hasMore, filtered, known: masterCount(columns, type) });
    const totalConfigs = totalOf(rows.length, 'config');
    const paging = {
        total: totalConfigs,
        filtered,
        hasMore: roots.hasMore,
        isLoadingMore: roots.isLoadingMore,
        onLoadMore: roots.loadMore,
    };

    // Memoised so an unrelated re-render (a keystroke, a selection toggle, another query
    // settling) does not hand `PageContainer` a new `actions` element every time — `patch` is
    // now a genuinely stable `useCallback` (`useUrlFilter`) so it is listed like any other dep;
    // `pulls` is stable in behaviour though not always in reference, so only its own primitives
    // are listed instead.
    const actions = useMemo(
        () => (
            <MatrixPageActions
                isMobile={isMobile}
                searchPlaceholder="Find a config"
                search={filters.search}
                onSearchChange={(search) => patch({ search })}
                newButton={
                    <Button component={Link} to={NEW_CONFIG_PATH} leftSection={<IconPlus size={15} stroke={1.7} />}>
                        New config
                    </Button>
                }
                pulling={pulls.pullingAll}
                onPullAll={() => pulls.pullAll(environmentNames)}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `pulls` is stable in behaviour, not reference
        [isMobile, filters.search, patch, pulls.pullingAll, environmentNames.join(',')],
    );

    return (
        <PageContainer
            title="Configs"
            count={roots.isSuccess && totalConfigs !== null ? countOf('config', totalConfigs) : undefined}
            actions={actions}
        >
            <Stack gap="sm">
                {isMobile && <MobileFilters value={filters} onChange={patch} placeholder="Find a config" />}

                <PullFailedAlert columns={columns} />

                <MatrixLoadState
                    isLoading={roots.isLoading}
                    isError={roots.isError && rows.length === 0}
                    error={roots.error}
                    onRetry={roots.refetch}
                    label="configs"
                />

                {roots.isSuccess && rows.length === 0 && (
                    <MatrixEmptyState
                        type="config"
                        reason={emptyReason(columns, filtered)}
                        columns={columns}
                        onClearFilters={clearFilters}
                        onAdopt={({ environment, count }) =>
                            matrix.adoption.adopt({ environment, count, scope: 'all' })
                        }
                        adopting={matrix.adoption.isPending}
                    />
                )}

                {rows.length > 0 &&
                    (isMobile ? (
                        <MatrixCardList
                            spec={CONFIG_ROWS}
                            columns={shownColumns}
                            rows={rows}
                            selection={selection}
                            onOpen={setDrawer}
                            paging={paging}
                        />
                    ) : (
                        <MatrixGrid
                            spec={CONFIG_ROWS}
                            columns={shownColumns}
                            rows={rows}
                            selection={selection}
                            onOpen={setDrawer}
                            paging={paging}
                            {...gridChrome(matrix, filters, patch, columns, rows)}
                        />
                    ))}
            </Stack>

            <MatrixOverlays state={matrix} environments={environmentNames} />
        </PageContainer>
    );
};
